"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { Check, Copy, Sparkles } from "@/components/icons";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { aiSummaryLinks } from "@/components/site/footer/data";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TOOLTIP_DISMISSED_KEY = "ai_summary_tooltip_dismissed";

interface Props {
  /** Path of the page's markdown endpoint (e.g. "/api/md/blog/my-post").
      When set, the menu gains a "Copy as Markdown" item. */
  mdPath?: string;
}

export function AiSummary({ mdPath }: Props) {
  const [open, setOpen] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [mounted, setMounted] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [showHint, setShowHint] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const reduceMotion = useReducedMotion();
  const posthog = usePostHog();
  const tooltipTestEnabled = useFeatureFlagEnabled("blog-ai-summary-tooltip") === true;

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!tooltipTestEnabled || open) return;
    try {
      if (sessionStorage.getItem(TOOLTIP_DISMISSED_KEY)) return;
    } catch {
      // ignore storage access errors
    }
    const timer = setTimeout(() => {
      setShowHint(true);
      posthog?.capture("ai_summary_tooltip_shown");
    }, 1500);
    return () => clearTimeout(timer);
  }, [tooltipTestEnabled, open, posthog]);

  const dismissHint = () => {
    setShowHint(false);
    try {
      sessionStorage.setItem(TOOLTIP_DISMISSED_KEY, "1");
    } catch {
      // ignore storage access errors
    }
  };

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const update = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const getUrl = () => (typeof window !== "undefined" ? window.location.href : "");

  const handleCopyMarkdown = async () => {
    if (!mdPath) return;
    try {
      const res = await fetch(mdPath);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
    setTimeout(() => setCopyState("idle"), 2000);
  };

  const menu = (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={menuRef}
          role="menu"
          initial={{ opacity: 0, y: reduceMotion ? 0 : -4, scale: reduceMotion ? 1 : 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: reduceMotion ? 0 : -4, scale: reduceMotion ? 1 : 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{ position: "fixed", top: coords.top, right: coords.right, zIndex: 9999 }}
          className="border-border bg-background/95 w-52 rounded-xl border p-1.5 shadow-lg backdrop-blur-md"
        >
          <p className="text-muted-foreground px-2.5 py-1.5 text-[11px] font-medium tracking-wide uppercase">
            Summarize with AI
          </p>
          <div className="flex flex-col">
            {aiSummaryLinks.map(({ name, label, buildUrl, icon }) => (
              <button
                key={name}
                type="button"
                role="menuitem"
                onClick={() => {
                  window.open(buildUrl(getUrl()), "_blank", "noopener,noreferrer");
                  setOpen(false);
                }}
                aria-label={label}
                className={cn(
                  "text-foreground/90 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm",
                  "hover:bg-brand/5 hover:text-foreground text-left transition-colors"
                )}
              >
                <span className="shrink-0 opacity-80">{icon}</span>
                {name}
              </button>
            ))}
          </div>
          {mdPath && (
            <>
              <div className="bg-border my-1 h-px" />
              <button
                type="button"
                role="menuitem"
                onClick={handleCopyMarkdown}
                className={cn(
                  "text-foreground/90 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm",
                  "hover:bg-brand/5 hover:text-foreground text-left transition-colors"
                )}
              >
                {copyState === "copied" ? (
                  <Check size={16} className="text-brand shrink-0" />
                ) : (
                  <Copy size={16} className="text-muted-foreground shrink-0" />
                )}
                {copyState === "copied"
                  ? "Copied"
                  : copyState === "error"
                    ? "Copy failed"
                    : "Copy as Markdown"}
              </button>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) {
      posthog?.capture("ai_summary_clicked", { tooltip_shown: showHint });
    }
    if (showHint) dismissHint();
  };

  const buttonProps = {
    ref: buttonRef,
    type: "button" as const,
    onClick: handleToggle,
    "aria-haspopup": "menu" as const,
    "aria-expanded": open,
    className:
      "cta-glow-btn-inner !h-auto cursor-pointer gap-2 !rounded-lg !px-3 !py-1.5 !text-sm !font-medium",
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <span className="cta-glow-btn-wrap">
        {tooltipTestEnabled ? (
          <TooltipProvider delay={0} closeDelay={0}>
            <Tooltip
              open={showHint}
              onOpenChange={(next) => {
                if (!next) dismissHint();
              }}
            >
              <TooltipTrigger {...buttonProps}>
                <Sparkles size={13} />
                AI Summary
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs font-semibold">
                Bored already? 😴 Let AI read this for you
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <button {...buttonProps}>
            <Sparkles size={13} />
            AI Summary
          </button>
        )}
      </span>
      {mounted ? createPortal(menu, document.body) : null}
    </div>
  );
}
